//
// RecordExtensionPickPanel
//
// Pick a sibling/child extension (or base definition) by name for set/transfer/clone-from.
//

import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useStore } from '../stores'

import { FixedVSpace, VScrollView, VView } from '../appview'
import { Header, Icon, Item, Menu, MenuItem, Text } from '../components'
import {
    formatExtensionGroupLabel,
    groupExtensionsFromDefinitions,
} from '../stores/data/extension-groups'
import { changeDefinitionLostDataFields } from '../stores/data/utils'
import { ItemTitle } from '../workspaces/components'

const ConfirmSubmenu = observer(function ConfirmSubmenu({
    lostFields,
    confirmLabel,
    onConfirm,
    onCancel,
}) {
    const { app } = useStore()
    return (
        <VView className="actions-panel recorddefinition-change-panel">
            <div className="inspector">
                <Header className="cc-danger">{app.text('Warning')}</Header>
                <Text>
                    {app.text(
                        `Changing the extension can result in data loss
                         when fields are no longer available in the new
                         definition. This data cannot be recovered by
                         changing back to the previous extension.`
                    )}
                </Text>
                {lostFields.length ? (
                    <>
                        <Text>
                            {app.text(
                                `For records that use this definition,
                                 these fields will be lost:`
                            )}
                        </Text>
                        <VScrollView className="lost-fields">
                            {lostFields.map(field => (
                                <Item key={field.gid}>
                                    <Icon name="field" size="text" />{' '}
                                    <ItemTitle item={field} ellipsisAfter={32} />
                                </Item>
                            ))}
                        </VScrollView>
                        <FixedVSpace size={5} />
                    </>
                ) : undefined}
            </div>
            <Menu>
                <MenuItem onClick={onCancel}>{app.text('Cancel')}</MenuItem>
                <MenuItem className="cc-danger" onClick={onConfirm}>
                    {confirmLabel || app.text('Yes, change the extension')}
                </MenuItem>
            </Menu>
        </VView>
    )
})

export const RecordExtensionPickPanel = observer(function RecordExtensionPickPanel({
    worksheet,
    title,
    description,
    excludeCurrent = true,
    onlyExtended = false,
    currentIsTarget = false,
    confirmLabel,
    alwaysConfirm = false,
    onPick,
    onCancel,
}) {
    const { app, data } = useStore()
    const record = worksheet.record
    const currentDefinition = data.definitions.get(record.definition)

    const [selectedGid, setSelectedGid] = useState(null)

    let groups = groupExtensionsFromDefinitions(worksheet.sibling_definitions)
    if (excludeCurrent && currentDefinition) {
        groups = groups.filter(group => group.definitionGid !== currentDefinition.gid)
    }
    if (onlyExtended) {
        groups = groups.filter(group => group.isExtended)
    }

    const selectedDefinition = selectedGid ? data.definitions.get(selectedGid) : null
    const lostFields =
        selectedDefinition && currentDefinition
            ? changeDefinitionLostDataFields(
                  currentIsTarget ? currentDefinition : selectedDefinition,
                  currentIsTarget ? selectedDefinition : currentDefinition,
                  data
              )
            : []

    const _onPick = () => {
        onPick && selectedGid && onPick(selectedGid)
    }

    const _onCancel = () => {
        onCancel && onCancel()
    }

    const notExtendedSuffix = `, ${app.text('not extended')}`

    if (!groups.length) {
        return (
            <VView className="actions-panel recorddefinition-change-panel">
                <div className="inspector">
                    <Header>{title || app.text('Choose extension')}</Header>
                    <Text>
                        {currentDefinition && currentDefinition.is_extended
                            ? app.text('No other extensions available')
                            : app.text('No extensions available')}
                    </Text>
                </div>
                <Menu>
                    <MenuItem onClick={_onCancel}>{app.text('Cancel')}</MenuItem>
                </Menu>
            </VView>
        )
    }

    return (
        <VView className="actions-panel recorddefinition-change-panel">
            <div className="inspector">
                <Header>{title || app.text('Choose extension')}</Header>
                {description ? <Text>{description}</Text> : undefined}
                <VScrollView
                    className="search-results"
                    style={{ backgroundColor: 'white', maxHeight: 200 }}
                >
                    {groups.map(group => (
                        <Item
                            key={group.key}
                            selected={selectedGid === group.definitionGid}
                            onClick={() => setSelectedGid(group.definitionGid)}
                        >
                            {formatExtensionGroupLabel(group, notExtendedSuffix)}
                        </Item>
                    ))}
                </VScrollView>
            </div>
            <Menu>
                <MenuItem
                    disabled={!selectedGid}
                    className={lostFields.length || alwaysConfirm ? 'cc-notice' : ''}
                    submenu={
                        lostFields.length || alwaysConfirm ? (
                            <ConfirmSubmenu
                                lostFields={lostFields}
                                confirmLabel={confirmLabel}
                                onConfirm={_onPick}
                                onCancel={_onCancel}
                            />
                        ) : undefined
                    }
                    onClick={
                        lostFields.length || alwaysConfirm ? undefined : () => _onPick()
                    }
                >
                    {confirmLabel || app.text('Choose')}
                </MenuItem>
                <MenuItem onClick={_onCancel}>{app.text('Cancel')}</MenuItem>
            </Menu>
        </VView>
    )
})
